import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { TID } from "@/lib/testIds";
import BrandStripe from "./BrandStripe";
import { GoodEyesLogo } from "./GoodEyesLogo";

const MARQUEE_ITEMS = [
  "Designed in New Zealand",
  "Recycled Co-Polyester Frames",
  "Recycled Lenses",
  "BPA Free",
  "Lightweight Construction",
  "Fashion Forward Shapes",
  "Unisex Styling",
  "Colour-Coded Strengths",
  "Recycled Felt Pouch",
  "Fully Recyclable Packaging",
];

const dotColors = ["var(--ge-yellow)","var(--ge-red)","var(--ge-green)","var(--ge-navy)","var(--ge-beige)"];

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid={TID.hero.root}
      className="relative min-h-[92vh] pt-40 md:pt-52 pb-28 overflow-hidden bg-white"
    >
      <div className="ge-container grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-center">
        {/* Left: copy */}
        <div className="md:col-span-7 relative z-10">
          {/* GOOD + tagline is now the hero display block */}
          <motion.div
            initial="hidden"
            animate="show"
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.3, delayChildren: 0.1 } } }}
            className="flex flex-col items-start gap-6 md:gap-8"
            data-testid="hero-good-tagline"
          >
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="w-full"
            >
              <GoodEyesLogo height={110} className="md:hidden" />
              <GoodEyesLogo height={170} className="hidden md:block" />
            </motion.div>
            <div className="display-font font-extrabold leading-[1.02] text-[color:var(--ge-navy)] text-[clamp(2.4rem,5.5vw,4.6rem)] tight-cap">
              {[
                { t: "for your vision.",  c: "var(--ge-navy)"  },
                { t: "for your style.",   c: "var(--ge-navy)"  },
                { t: "for the planet.",   c: "var(--ge-green)" },
              ].map((line) => (
                <motion.div
                  key={line.t}
                  variants={{ hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0 } }}
                  transition={{ duration: 0.6, ease: [0.19, 1, 0.22, 1] }}
                  style={{ color: line.c }}
                >
                  {line.t}
                </motion.div>
              ))}
            </div>
          </motion.div>

          <div className="mt-8 h-[6px] w-24 rounded-full" style={{ background: "var(--ge-yellow)" }} />

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.3 }}
            className="mt-8 max-w-xl text-lg md:text-xl font-light text-[color:var(--ge-navy)]/80 leading-relaxed"
            data-testid={TID.hero.tagline}
          >
            Fashion-forward reading glasses, designed in New&nbsp;Zealand and
            made from recycled materials. Colour-coded by strength, so
            choosing your pair is as simple as picking your favourite hue.
          </motion.p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/shop"
              data-testid={TID.hero.ctaPrimary}
              className="inline-flex items-center gap-2 rounded-full bg-[color:var(--ge-navy)] text-white px-7 py-4 text-xs uppercase tracking-[0.24em] font-semibold hover:bg-[color:var(--ge-red)] transition-colors"
            >
              Shop the range
              <ArrowRight size={16} />
            </Link>
            <a
              href="#strengths"
              data-testid={TID.hero.ctaSecondary}
              className="inline-flex items-center gap-2 rounded-full border-2 border-[color:var(--ge-navy)] px-7 py-4 text-xs uppercase tracking-[0.24em] font-semibold text-[color:var(--ge-navy)] hover:bg-[color:var(--ge-beige)] transition-colors"
            >
              Find your strength
            </a>
          </div>

          {/* Feature pills */}
          <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-[11px] uppercase tracking-[0.28em] text-[color:var(--ge-navy)]/80 font-semibold">
            <span className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: "var(--ge-beige)", boxShadow: "inset 0 0 0 1px rgba(0,31,91,0.2)" }} /> Design-led
            </span>
            <span className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: "var(--ge-yellow)" }} /> Colour-coded
            </span>
            <span className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: "var(--ge-green)" }} /> Sustainable
            </span>
          </div>
        </div>

        {/* Right: hero image (Insight Evergreen, front) */}
        <div className="md:col-span-5 relative flex items-center justify-center h-[380px] md:h-[560px]">
          <motion.img
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: [0.19, 1, 0.22, 1], delay: 0.4 }}
            src="/assets/hero/insight-evergreen-front.png"
            alt="Good Eyes Insight reading glasses in Evergreen"
            data-testid={TID.hero.image}
            className="relative z-10 w-full h-full object-contain product-img"
            loading="eager"
          />
          <div className="absolute bottom-2 left-0 text-[10px] uppercase tracking-[0.28em] text-[color:var(--ge-navy)]/70 font-semibold">
            Style / Insight / Evergreen
          </div>
          <div className="absolute bottom-2 right-0 text-[10px] uppercase tracking-[0.28em] text-[color:var(--ge-navy)]/70 font-semibold">
            $65 NZD
          </div>
        </div>
      </div>

      {/* Ticker bottom */}
      <div className="absolute bottom-[22px] left-0 right-0 border-t border-[color:var(--ge-navy)]/15 py-3 overflow-hidden bg-white">
        <div className="marquee flex whitespace-nowrap gap-14 text-[11px] uppercase tracking-[0.4em] text-[color:var(--ge-navy)]/80 font-semibold">
          {Array.from({ length: 2 }).map((_, r) => (
            <div key={r} className="flex items-center gap-14 pr-14">
              {MARQUEE_ITEMS.map((label, i) => (
                <React.Fragment key={label}>
                  <span>{label}</span>
                  <span aria-hidden style={{ color: dotColors[i % dotColors.length] }}>◆</span>
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0">
        <BrandStripe height={22} />
      </div>
    </section>
  );
};

export default Hero;
